/**
 * Console API client utilities.
 * Calls sam-dbam /dbam/v1/recipients/* endpoints.
 */
import { getOrgContext } from './cloudApi';

function getBaseUrl() {
    const url = process.env.REACT_APP_SAM_DBAM_URL;
    if (!url) throw new Error('REACT_APP_SAM_DBAM_URL not set');
    return url.replace(/\/+$/, '') + '/dbam/v1';
}

function toQuery(params = {}) {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        usp.set(key, String(value));
    });
    const qs = usp.toString();
    return qs ? `?${qs}` : '';
}

/**
 * Map recipient_id to user_id for backward compatibility with Console UI
 */
function mapRecipientIdToUserId(payload) {
    if (payload === null || payload === undefined) return payload;

    if (Array.isArray(payload)) {
        return payload.map(mapRecipientIdToUserId);
    }

    if (typeof payload !== 'object') return payload;

    const out = { ...payload };

    // Map global_recipient_id or recipient_id → user_id for Console UI compatibility
    if (Object.prototype.hasOwnProperty.call(out, 'global_recipient_id')) {
        out.user_id = out.global_recipient_id;
    } else if (Object.prototype.hasOwnProperty.call(out, 'recipient_id')) {
        out.user_id = out.recipient_id;
    }

    Object.keys(out).forEach((k) => {
        if (typeof out[k] === 'object' && out[k] !== null) {
            out[k] = mapRecipientIdToUserId(out[k]);
        }
    });

    return out;
}

async function request(path, { method = 'GET', headers = {}, body } = {}) {
    const url = `${getBaseUrl()}${path}`;
    // Inject org context headers so the backend can scope to the caller's org.
    const ctx = getOrgContext();
    const orgH = {};
    if (ctx.orgId) orgH['X-Org-ID'] = ctx.orgId;
    if (ctx.userId) orgH['X-User-ID'] = ctx.userId;
    const init = {
        method,
        headers: {
            Accept: 'application/json',
            ...orgH,
            ...headers,
        },
    };

    if (body !== undefined) {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
    }

    const res = await fetch(url, init);

    if (res.status === 204) return null;

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const rawData = isJson ? await res.json() : await res.text();

    if (!res.ok) {
        let message;
        if (rawData && typeof rawData === 'object') {
            if (rawData.error && typeof rawData.error === 'object') {
                // sam-dbam format: { error: { code, message }, meta: {...} }
                message = rawData.error.message || rawData.error.code || JSON.stringify(rawData.error);
            } else {
                message = rawData.message || (typeof rawData.error === 'string' && rawData.error);
            }
        }
        if (!message) {
            message = (typeof rawData === 'string' && rawData) || `Request failed with status ${res.status}`;
        }
        const err = new Error(message);
        err.status = res.status;
        err.data = rawData;
        throw err;
    }

    return mapRecipientIdToUserId(rawData);
}

/**
 * Fetch users (recipients) with optional search and pagination.
 * Returns {items: Array} for Console UI compatibility.
 */
export async function fetchUsers(search = '', limit = 100, offset = 0, sortBy = 'email', sortOrder = 'ASC', orgId = '') {
    // Map to sam-dbam expected params: q, page, perPage, sort_by, sort_order
    const page = Math.floor(offset / Math.max(limit, 1)) + 1;
    const qs = toQuery({ q: search, page, perPage: limit, sort_by: sortBy, sort_order: sortOrder, org_id: orgId });
    const resp = await request(`/recipients${qs}`);
    // sam-dbam returns {data: {data: [...], pagination: {...}}, meta: {...}}
    // Console expects {items: [...]}
    const items = resp?.data?.data || resp?.data || [];
    return { items: mapRecipientIdToUserId(items) };
}

/**
 * Fetch only staged recipients (staged = true).
 * Returns {items: Array} for Console UI compatibility.
 */
export async function fetchStagedRecipients(search = '', limit = 1000, sortBy = 'email', sortOrder = 'ASC', orgId = '') {
    const qs = toQuery({ q: search, staged: 'true', perPage: limit, sort_by: sortBy, sort_order: sortOrder, org_id: orgId });
    const resp = await request(`/recipients${qs}`);
    const items = resp?.data?.data || resp?.data || [];
    return { items: mapRecipientIdToUserId(items) };
}

/**
 * Create a new user (recipient).
 */
export async function createUser(email, name, upsert = false, phoneNumber = '') {
    const body = { email, name, upsert };
    if (phoneNumber) body.phone_number = phoneNumber;
    const resp = await request('/recipients', {
        method: 'POST',
        body,
    });
    // sam-dbam returns {data: {...}, meta: {...}}
    return resp?.data || resp;
}

/**
 * Update an existing user (recipient).
 */
export async function updateUser(userId, data) {
    const resp = await request(`/recipients/${encodeURIComponent(String(userId))}`, {
        method: 'PATCH',
        body: data,
    });
    return resp?.data || resp;
}

/**
 * Delete a user (recipient) by ID.
 */
export async function deleteUser(userId) {
    const resp = await request(`/recipients/${encodeURIComponent(String(userId))}`, {
        method: 'DELETE',
    });
    return resp?.data || resp;
}

/**
 * Fetch all tags.
 * Returns {items: Array} for Console UI compatibility.
 */
export async function fetchTags() {
    const resp = await request('/recipients/tags');
    // sam-dbam returns {data: [...], meta: {...}}
    const items = resp?.data || [];
    return { items };
}

/**
 * Create a new tag.
 */
export async function createTag(tagName, tagColor = '#6b7280') {
    const resp = await request('/recipients/tags', {
        method: 'POST',
        body: { tag_name: tagName, tag_color: tagColor },
    });
    return resp?.data || resp;
}

/**
 * Delete a tag by ID.
 */
export async function deleteTag(tagId) {
    const resp = await request(`/recipients/tags/${encodeURIComponent(String(tagId))}`, {
        method: 'DELETE',
    });
    return resp?.data || resp;
}

/**
 * Fetch tags assigned to a user (recipient).
 * Uses the dedicated /recipients/{id}/tags endpoint.
 * Returns {items: Array} for Console UI compatibility.
 */
export async function fetchUserTags(userId) {
    const resp = await request(`/recipients/${encodeURIComponent(String(userId))}/tags`);
    // sam-dbam returns {data: [...], meta: {...}}
    const tags = resp?.data || resp || [];
    return { items: Array.isArray(tags) ? tags : [] };
}

/**
 * Set user's tag assignments (sync desired tags with current tags).
 * This fetches current tags via /recipients/{id}/tags, computes diff,
 * and calls assign/remove as needed.
 */
export async function setUserTags(userId, tagIds) {
    // Fetch current tags for the user via the dedicated tags endpoint
    const currentResp = await request(`/recipients/${encodeURIComponent(String(userId))}/tags`);
    const currentTags = currentResp?.data || currentResp || [];
    const currentTagIds = new Set((Array.isArray(currentTags) ? currentTags : []).map(t => t.tag_id || t.id));
    const desiredTagIds = new Set(tagIds);

    // Find tags to add (in desired but not current)
    const toAdd = tagIds.filter(tid => !currentTagIds.has(tid));
    // Find tags to remove (in current but not desired)
    const toRemove = [...currentTagIds].filter(tid => !desiredTagIds.has(tid));

    // Add new tags
    for (const tid of toAdd) {
        await request(`/recipients/${encodeURIComponent(String(userId))}/tags`, {
            method: 'POST',
            body: { tag_id: tid },
        });
    }

    // Remove old tags
    for (const tid of toRemove) {
        await request(`/recipients/${encodeURIComponent(String(userId))}/tags/${encodeURIComponent(tid)}`, {
            method: 'DELETE',
        });
    }

    return { added: toAdd.length, removed: toRemove.length };
}

/**
 * Link a global recipient to a deployment (creates a deployment-scoped recipient).
 * Uses sam-dbam POST /deployments/{id}/users
 */
export async function linkUserToDeployment(deploymentId, userId, email, name) {
    return request(`/deployments/${encodeURIComponent(deploymentId)}/users`, {
        method: 'POST',
        body: { user_id: userId, email: email || '', name: name || '' },
    });
}

/**
 * Unlink a global recipient from a deployment (removes deployment-scoped recipient).
 * Uses sam-dbam DELETE /deployments/{id}/users/{userId}
 */
export async function unlinkUserFromDeployment(deploymentId, userId) {
    return request(`/deployments/${encodeURIComponent(deploymentId)}/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
    });
}

// Removed (WP3): linkSurveyToStaging stub.
// Staging is now server state: dragging a deployed survey into the Staging Area
// calls the sam-dbam Stage endpoint (deployed → staged) via
// stageDeploymentInCloud() in cloudApi.js, driven from useStagingArea.linkSurvey.

// Removed: sendStagingSurveys stub.
// The staging-area Send button now calls ChannelGate /outreach directly via
// sendOutreach() in outreachApi.js (the same path SendModal uses). See
// useStagingArea.js sendAll().
